import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import { stringSorter, dateSorter, formatDateHoursMinutes } from "utils/general"
import TableReporting from "components/Tables/TableReporting"

const CallFeedbackTable = props => {
  // Main title, excel and pdf name
  const title = props.t("Video call feedback")

  //Table data state
  const [series, setSeries] = useState([])
  const [origSeries, setOrigSeries] = useState([])
  // Filtered data
  const [filteredData, setFilteredData] = useState(series)
  const [handleValue, setHandleValue] = useState("")
  const [loading, setLoading] = useState(true)

  // Group by client and compute average rating %, latest rating and date
  useEffect(() => {
    if (props.data?.list?.length > 0) {
      const grouped = {}
      for (const item of props.data.list) {
        const client = item.client_name
        if (!grouped[client]) {
          grouped[client] = []
        }
        grouped[client].push(item)
      }

      const transformed = Object.keys(grouped).map(client => {
        const items = grouped[client]
        // Average rating in %
        const avg = Math.round(
          (items.reduce((sum, cur) => sum + cur.rating, 0) / items.length / 2) *
            100
        )
        // Latest feedback based on date
        const latestItem = items.reduce((latest, cur) =>
          new Date(cur.date) > new Date(latest.date) ? cur : latest
        )
        return {
          group: latestItem.group,
          client_name: client,
          avgRating: avg + "%",
          latestRating: latestItem.rating,
          latestDate: latestItem.date,
        }
      })
      console.log("Table data", props.data.list, transformed)
      setSeries(transformed)
      setOrigSeries(transformed)
      setLoading(false)
    } else {
      setSeries([])
    }
  }, [props.data])

  useEffect(() => {
    // console.log("entries filtered", series)
    if (series != undefined) {
      setFilteredData(series)
      if (handleValue != "") {
        handlingInput(handleValue)
      }
    }
  }, [series])

  // Convert individual ratings (0,1,2) to label and color
  const ratingBadgeFormatter = ratingValue => {
    let label,
      style = { fontSize: "10px", fontWeight: "normal", padding: "6px 10px" }
    switch (ratingValue) {
      case 0:
        label = props.t("Improvable")
        style.backgroundColor = "#E57373"
        break
      case 1:
        label = props.t("Satisfied")
        style.backgroundColor = "#F3B562"
        break
      case 2:
        label = props.t("Excellent")
        style.backgroundColor = "#50A684"
        break
      default:
        label = ""
    }
    return (
      <span className="badge font-size-12" style={style}>
        {label}
      </span>
    )
  }

  // Table columns
  const columns = [
    {
      dataIndex: "group",
      title: props.t("Group"),
      sorter: (a, b) => stringSorter(a, b, "group"),
      width: "170px",
      ellipsis: true,
    },
    {
      dataIndex: "client_name",
      title: props.t("Client name"),
      sorter: (a, b) => stringSorter(a, b, "client_name"),
      width: "220px",
      ellipsis: true,
    },
    {
      dataIndex: "avgRating",
      title: props.t("Average rating"),
      sorter: (a, b) => Number.parseInt(a.avgRating) - Number.parseInt(b.avgRating),
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "latestRating",
      title: props.t("Latest rating"),
      render: rating => ratingBadgeFormatter(rating),
      sorter: (a, b) => a.latestRating - b.latestRating,
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "latestDate",
      title: props.t("Latest date"),
      render: date => formatDateHoursMinutes(date),
      sorter: (a, b) => dateSorter(a.latestDate, b.latestDate),
      width: "140px",
      ellipsis: true,
    },
  ]
  const pdfHeading = [
    props.t("Group"),
    props.t("Client name"),
    props.t("Average rating"),
    props.t("Latest rating"),
    props.t("Latest date"),
  ]

  const pdfData = filteredData.map(item => [
    item.group,
    item.client_name,
    item.avgRating,
    ratingBadgeFormatter(item.latestRating).props.children,
    formatDateHoursMinutes(item.latestDate),
  ])

  const xlsxHeading = [pdfHeading]

  const xlsxData = filteredData.map(item => [
    item.group,
    item.client_name,
    item.avgRating,
    ratingBadgeFormatter(item.latestRating).props.children,
    formatDateHoursMinutes(item.latestDate),
  ])

  const handlingInput = value => {
    let result = []
    result = series.filter(
      data =>
        data.client_name?.toLowerCase().includes(value) ||
        data.group?.toLowerCase().includes(value)
    )

    setFilteredData(result)

    // Set original data result if value ""
    if (value == "") {
      setFilteredData(series)
    }
  }

  // Handle search function
  const handleSearch = event => {
    let value = event.target.value.toLowerCase()
    setHandleValue(value)
    handlingInput(value)
  }

  return (
    <div>
      <TableReporting
        filter={true}
        origSeries={origSeries}
        setSeries={setSeries}
        title={title}
        columns={columns}
        seriesFiltered={filteredData}
        xlsxHeading={xlsxHeading}
        xlsxData={xlsxData}
        pdfHeading={pdfHeading}
        pdfData={pdfData}
        handleSearch={handleSearch}
        loading={loading}
      ></TableReporting>
    </div>
  )
}

CallFeedbackTable.propTypes = {
  t: PropTypes.any,
  data: PropTypes.any,
}

export default withTranslation()(CallFeedbackTable)
